import { LazyIntlProvider } from '@hudl/statsbomb-frontends-adapter';
import i18n from 'i18next';
import { ReactNode } from 'react';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE_CODE } from '@/consts/i18n';
import enJson from '@/i18n/en';
import { useI18nBridge } from '@/i18n/i18nextBridge';
import { tryParseLocaleCode } from '@/utils/i18n';

// Seeds LazyIntlProvider with the URL locale segment for the initial CDN fetch
// - This will not be present when just loading the app on the root url
// - This hardcoded url locale will override any user config/cookies
const parseLocaleFromPath = (pathname: string): string => {
  const segment = (pathname ?? '').split('/')[1];
  if (!segment) return '';
  return tryParseLocaleCode(segment) ?? '';
};

const initialLocale = parseLocaleFromPath(window?.location?.pathname);

// Bundled English is the fallback for keys added locally but not yet deployed to the CDN.
// CDN translations overwrite these via addResourceBundle(..., true, true) in useI18nBridge.
i18n.use(initReactI18next).init({
  // This is a slightly hacky workaround for a bug in i18next
  // - Typescript definitions tell us that `i18n.language` is always a string but
  //     in practice it can be undefined when the language is not yet initialized.
  // - By manually forcing an initial locale here we can avoid the undefined case
  //     and prevent anyone reading this on the initial app hotpath from throwing.
  lng: initialLocale,
  resources: { en: { ...enJson } },
  fallbackLng: DEFAULT_LOCALE_CODE,
});

const I18nBridge = () => {
  useI18nBridge();
  return null;
};

export const I18nProvider = ({ children }: { children: ReactNode }) => (
  <LazyIntlProvider translationSets={['parachute-ui']} locale={initialLocale}>
    <I18nBridge />
    {children}
  </LazyIntlProvider>
);
